/**
 * Role Validation Hook
 * Validates user role from backend on-demand
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { authAPI } from '../services/api';
import { setUser } from '../store/slices/authSlice';
import { logError, logWarning } from '../utils/errorLogger';

interface UseRoleValidationOptions {
  requiredRole?: 'admin' | 'host' | 'guest' | 'usher';
  cacheDuration?: number; // Cache duration in milliseconds (default: 30 seconds)
  skipIfCached?: boolean; // Skip validation if cached result exists
}

interface UseRoleValidationReturn {
  isValidating: boolean;
  isValid: boolean | null; // null = not validated yet, true/false = validation result
  userRole: string | null;
  error: string | null;
  validate: () => Promise<void>; // Manual validation trigger
}

// Global cache to prevent duplicate API calls across components
const validationCache = new Map<string, { role: string; timestamp: number; cacheDuration: number }>();

export const useRoleValidation = (
  options: UseRoleValidationOptions = {}
): UseRoleValidationReturn => {
  const { requiredRole, cacheDuration = 30000, skipIfCached = true } = options;
  
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, tokens } = useAppSelector((state) => state.auth);
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const validationInProgressRef = useRef(false);
  const cacheKeyRef = useRef<string | null>(null);

  // Generate cache key based on user ID and required role
  useEffect(() => {
    if (user?.id) {
      cacheKeyRef.current = `role_validation_${user.id}_${requiredRole || 'any'}`;
    }
  }, [user?.id, requiredRole]);

  const validate = useCallback(async () => {
    // Don't validate if not authenticated
    if (!isAuthenticated || !user || !tokens) {
      setIsValid(false);
      setError('Not authenticated');
      return;
    }

    // Check cache first
    if (skipIfCached && cacheKeyRef.current) {
      const cached = validationCache.get(cacheKeyRef.current);
      if (cached) {
        const age = Date.now() - cached.timestamp;
        if (age < cached.cacheDuration) {
          // Use cached result
          setUserRole(cached.role);
          setIsValid(requiredRole ? cached.role === requiredRole : true);
          setError(null);
          return;
        } else {
          // Cache expired, remove it
          validationCache.delete(cacheKeyRef.current);
        }
      }
    }

    // Prevent duplicate concurrent validations
    if (validationInProgressRef.current) {
      return;
    }

    validationInProgressRef.current = true;
    setIsValidating(true);
    setError(null);

    try {
      // Fetch user profile from backend
      const response = await authAPI.getProfile();
      const verifiedUser = response.data.data;

      if (!verifiedUser || !verifiedUser.role) {
        throw new Error('Invalid user data from backend');
      }

      const backendRole = verifiedUser.role;
      setUserRole(backendRole);

      // Update Redux state if role changed
      if (user.role !== backendRole) {
        logWarning(`[SECURITY] Role mismatch detected! Updating to backend role.`);
        
        // Update Redux state (setUser will also update localStorage)
        dispatch(setUser(verifiedUser));
      }

      // Check if role matches required role
      const roleMatches = requiredRole ? backendRole === requiredRole : true;
      setIsValid(roleMatches);

      // Cache the result
      if (cacheKeyRef.current) {
        validationCache.set(cacheKeyRef.current, {
          role: backendRole,
          timestamp: Date.now(),
          cacheDuration,
        });
      }
    } catch (err: any) {
      logError('Role validation failed', err);
      setError(err.response?.data?.error?.message || err.message || 'Validation failed');
      setIsValid(false);
      
      // On 401/403, don't cache - these are auth errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        if (cacheKeyRef.current) {
          validationCache.delete(cacheKeyRef.current);
        }
      }
    } finally {
      setIsValidating(false);
      validationInProgressRef.current = false;
    }
  }, [isAuthenticated, user, tokens, requiredRole, cacheDuration, skipIfCached, dispatch]);

  // Auto-validate on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && user && tokens) {
      validate();
    }
  }, [isAuthenticated, validate, user, tokens]); // Only validate when auth state changes, not on every render

  return {
    isValidating,
    isValid,
    userRole,
    error,
    validate,
  };
};

export default useRoleValidation;

